import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies / Sweaters': 'ri-t-shirt-line',
  'T-Shirts': 'ri-t-shirt-line',
  'Jackets': 'ri-t-shirt-air-line',
  'Pants / Shorts': 'ri-palette-line',
  'Headwear': 'ri-user-star-line',
  'Sets': 'ri-exchange-line',
  'Underwear / Underpants': 'ri-shirt-line',
  'Jersey': 'ri-medal-line',
  'Accessories': 'ri-glasses-line',
  'Bags': 'ri-shopping-bag-3-line',
  'Belts': 'ri-link',
  'Perfumes': 'ri-drop-line',
  'Other Stuff': 'ri-more-2-line',
};

export default function Navbar() {
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDesktopDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleDesktopMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setDesktopDropdownOpen(true);
  }, []);

  const handleDesktopMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setDesktopDropdownOpen(false);
    }, 200);
  }, []);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-18">
            <h1>
              <Link
                to="/"
                className="flex items-center gap-2 group"
                onClick={closeMobileMenu}
              >
                <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
                  Kakobuy<span className="text-accent-500">Finds</span>
                </span>
              </Link>
            </h1>

            <div className="hidden md:flex items-center gap-6">
              <div
                ref={dropdownRef}
                className="relative"
                onMouseEnter={handleDesktopMouseEnter}
                onMouseLeave={handleDesktopMouseLeave}
              >
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                    desktopDropdownOpen
                      ? 'bg-accent-100 text-accent-800'
                      : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                  onClick={() => setDesktopDropdownOpen(!desktopDropdownOpen)}
                >
                  <span>Categories</span>
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${desktopDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {desktopDropdownOpen && (
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 rounded-xl border border-background-200/70 py-2 w-56 max-h-80 overflow-y-auto nav-dropdown-enter-active shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-accent-700 hover:bg-accent-50 transition-colors duration-150 cursor-pointer"
                      >
                        <i className={`${categoryIcons[cat.name] || 'ri-shopping-bag-line'} text-base w-5 flex items-center justify-center`}></i>
                        <span>{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer"
              >
                Kakobuy Sheet
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap px-5 py-2.5 rounded-full border-2 border-primary-500 text-primary-500 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-all duration-200 cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            <button
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer relative z-[60]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <i className={`text-xl transition-transform duration-200 ${mobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[55] md:hidden backdrop-blur-sm transition-opacity duration-300"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-[320px] max-w-[85vw] bg-background-50 z-[58] md:hidden transform transition-transform duration-350 ease-out flex flex-col shadow-[0_0_40px_rgba(0,0,0,0.08)] ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
          <span className="font-heading text-lg font-bold text-foreground-900">
            Categories
          </span>
          <button
            className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <div className="space-y-1">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-foreground-700 hover:text-accent-700 hover:bg-accent-50 active:bg-accent-100 transition-colors cursor-pointer group"
                onClick={closeMobileMenu}
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 group-hover:bg-accent-100 transition-colors flex-shrink-0">
                  <i className={`${categoryIcons[cat.name] || 'ri-shopping-bag-line'} text-base text-foreground-500 group-hover:text-accent-600`}></i>
                </div>
                <span className="text-sm font-medium">{cat.name}</span>
                <i className="ri-arrow-right-s-line text-foreground-300 ml-auto"></i>
              </a>
            ))}
          </div>
        </div>

        <div className="px-4 py-4 border-t border-background-200/70 flex flex-col gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 active:scale-[0.98] transition-all duration-200 cursor-pointer"
            onClick={closeMobileMenu}
          >
            <i className="ri-external-link-line"></i>
            Kakobuy Sheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border-2 border-primary-500 text-primary-500 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 active:scale-[0.98] transition-all duration-200 cursor-pointer"
            onClick={closeMobileMenu}
          >
            <i className="ri-shopping-cart-line"></i>
            Buy It Now
          </a>
        </div>
      </div>
    </>
  );
}